import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Calendar, Clock, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";

// --------------------- Star display + input ---------------------
export function Stars({ value = 0, size = 16, onChange }) {
  const editable = !!onChange;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <button key={i} type="button" disabled={!editable} onClick={()=>onChange?.(i)}
          data-testid={editable?`star-${i}`:undefined}
          className={editable?"transition hover:scale-110":""}>
          <Star style={{width:size, height:size}}
            className={i <= value ? "fill-[#FF7D54] text-[#FF7D54]" : "text-slate-300"}/>
        </button>
      ))}
    </div>
  );
}

// --------------------- Reviews block on public professional page ---------------------
export function ProfessionalReviews({ professionalId }) {
  const { user } = useAuth();
  const [data, setData] = useState({items:[], avg:0, count:0});
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = ()=> api.get("/reviews",{params:{professional_id: professionalId}}).then(r=>setData(r.data)).catch(()=>{});
  useEffect(() => { load(); }, [professionalId]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/reviews", {professional_id: professionalId, rating, comment});
      toast.success("Avaliação enviada! Aguardará moderação.");
      setOpen(false); setComment(""); setRating(5); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-8">
      <div className="flex items-end justify-between mb-3">
        <h2 className="text-xl font-bold" style={{fontFamily:"Plus Jakarta Sans"}}>Avaliações</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm"><Stars value={Math.round(data.avg)}/><b className="ml-1">{data.avg.toFixed(1)}</b><span className="text-slate-500">({data.count})</span></div>
          {user?.role === "associate" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="review-open-btn" size="sm" className="bg-[#0D7C66] hover:bg-[#074E41]">Avaliar</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Avaliar profissional</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">Sua nota: <Stars value={rating} size={28} onChange={setRating}/></div>
                  <Textarea data-testid="review-comment" rows={4} value={comment} onChange={e=>setComment(e.target.value)} placeholder="Compartilhe sua experiência (opcional)"/>
                  <p className="text-xs text-slate-500">Sua avaliação passará por moderação antes de ser publicada.</p>
                </div>
                <DialogFooter>
                  <Button data-testid="review-submit" onClick={submit} disabled={saving} className="bg-[#0D7C66] hover:bg-[#074E41]">{saving?"Enviando...":"Enviar avaliação"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      {data.items.length===0 ? (
        <div className="text-sm text-slate-500 rounded-2xl bg-slate-50 p-6 text-center">Ainda não há avaliações públicas para este profissional.</div>
      ) : (
        <div className="space-y-3">
          {data.items.map(r=>(
            <Card key={r.id} className="p-4 gs-shadow border-slate-200" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{r.author_name}</div>
                <Stars value={r.rating}/>
              </div>
              {r.comment && <div className="text-sm text-slate-700 mt-1">{r.comment}</div>}
              <div className="text-[10px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString("pt-BR")}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------- Public scheduling block on professional page ---------------------
export function ProfessionalScheduler({ professionalId, professionalName }) {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [form, setForm] = useState({name:"", phone:"", email:"", service:"", notes:"", consent:false});
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const load = ()=> api.get(`/professionals/${professionalId}/slots`).then(r=>setSlots(r.data));
  useEffect(() => { load(); }, [professionalId]);

  useEffect(()=>{
    if (user) setForm(f=>({...f, name: user.name||"", email: user.email||"", phone: user.phone||""}));
  }, [user]);

  const book = async () => {
    if (!form.consent) return toast.error("Autorize o compartilhamento dos dados para agendar.");
    setSaving(true);
    try {
      const { data } = await api.post(`/slots/${chosen.id}/book`, form);
      setConfirmed(data);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const grouped = slots.reduce((acc, s)=>{ (acc[s.date] ||= []).push(s); return acc; }, {});
  const days = Object.keys(grouped).sort();

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{fontFamily:"Plus Jakarta Sans"}}>
        <Calendar className="w-5 h-5 text-[#0D7C66]"/>Agendamento online
      </h2>
      {days.length === 0 ? (
        <div className="text-sm text-slate-500 rounded-2xl bg-slate-50 p-6 text-center">Este profissional ainda não publicou horários. Use o botão "Tenho Interesse" para solicitar contato direto.</div>
      ) : (
        <div className="space-y-4">
          {days.map(d=>(
            <div key={d} data-testid={`slot-day-${d}`}>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{new Date(d+"T00:00:00").toLocaleDateString("pt-BR", {weekday:"long", day:"2-digit", month:"long"})}</div>
              <div className="flex flex-wrap gap-2">
                {grouped[d].map(s=>(
                  <Button key={s.id} data-testid={`slot-${s.id}`} variant="outline" size="sm" onClick={()=>setChosen(s)} className="border-[#0D7C66]/40 text-[#0D7C66] hover:bg-[#E6F4F1]"><Clock className="w-3.5 h-3.5 mr-1"/>{s.time}</Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!chosen} onOpenChange={(v)=>{ if(!v){setChosen(null); setConfirmed(null);} }}>
        <DialogContent className="max-w-lg">
          {confirmed ? (
            <div className="py-2">
              <DialogHeader><DialogTitle>Agendamento confirmado!</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600 mt-2">Seu horário com {professionalName} foi reservado.</p>
              <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm space-y-1">
                <div><span className="text-slate-500">Protocolo:</span> <b data-testid="schedule-confirm-number">{confirmed.lead_number}</b></div>
                <div><span className="text-slate-500">Data:</span> <b>{chosen.date}</b></div>
                <div><span className="text-slate-500">Horário:</span> <b>{chosen.time}</b></div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={()=>{setChosen(null);setConfirmed(null);}}>Fechar</Button>
                <Button className="bg-[#0D7C66] hover:bg-[#074E41]" onClick={()=>window.location.href=`/pedidos/${confirmed.lead_number}`}>Acompanhar guia</Button>
              </DialogFooter>
            </div>
          ) : chosen && (
            <div className="space-y-3">
              <DialogHeader><DialogTitle>Agendar {chosen.date} às {chosen.time}</DialogTitle></DialogHeader>
              <Input data-testid="sched-name" placeholder="Nome completo" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="sched-phone" placeholder="Telefone/WhatsApp" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
                <Input data-testid="sched-email" type="email" placeholder="E-mail" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
              </div>
              <Input data-testid="sched-service" placeholder="Serviço (opcional)" value={form.service} onChange={e=>setForm({...form,service:e.target.value})}/>
              <Textarea data-testid="sched-notes" placeholder="Observação (opcional)" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
              <label className="flex gap-2 items-start text-xs text-slate-600">
                <Checkbox data-testid="sched-consent" checked={form.consent} onCheckedChange={v=>setForm({...form,consent:!!v})}/>
                <span>Autorizo o compartilhamento dos dados com o profissional para confirmação do agendamento.</span>
              </label>
              <DialogFooter>
                <Button data-testid="sched-submit" onClick={book} disabled={saving} className="bg-[#FF7D54] hover:bg-[#e56a44]">{saving?"Agendando...":"Confirmar horário"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --------------------- Professional panel: manage slots ---------------------
export function ProSlotsManager() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(30);
  const load = ()=> api.get("/professionals/me/slots").then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!date || !time) return toast.error("Informe data e horário");
    try {
      await api.post("/professionals/me/slots",{date, time, duration_min: duration});
      toast.success("Horário publicado");
      setTime(""); load();
    } catch { toast.error("Erro ao publicar"); }
  };
  const del = async (sid) => { await api.delete(`/professionals/me/slots/${sid}`); load(); };

  return (
    <Card className="p-6 gs-shadow space-y-4" data-testid="pro-slots">
      <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold">Agendamento online</div>
      <h2 className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Meus horários</h2>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,120px,auto] gap-2 items-end">
        <div><label className="text-xs text-slate-500">Data</label><Input data-testid="slot-date" type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
        <div><label className="text-xs text-slate-500">Horário</label><Input data-testid="slot-time" type="time" value={time} onChange={e=>setTime(e.target.value)}/></div>
        <div><label className="text-xs text-slate-500">Duração (min)</label><Input data-testid="slot-duration" type="number" min="15" step="15" value={duration} onChange={e=>setDuration(+e.target.value)}/></div>
        <Button data-testid="slot-add" onClick={add} className="bg-[#0D7C66] hover:bg-[#074E41]"><Plus className="w-4 h-4 mr-1"/>Publicar</Button>
      </div>
      <div className="mt-2 space-y-2">
        {items.length===0 && <div className="text-sm text-slate-500">Nenhum horário publicado ainda.</div>}
        {items.map(s=>(
          <div key={s.id} className="flex items-center justify-between border-b border-slate-100 py-2" data-testid={`myslot-${s.id}`}>
            <div className="flex items-center gap-3 text-sm"><Calendar className="w-4 h-4 text-slate-500"/><b>{s.date}</b><Clock className="w-4 h-4 text-slate-500"/><b>{s.time}</b><span className="text-slate-500">({s.duration_min}min)</span></div>
            <div className="flex items-center gap-2">
              <Badge className={s.status==="available"?"bg-[#E6F4F1] text-[#0D7C66] border-0":"bg-slate-100 text-slate-600 border-0"}>{s.status==="available"?"Disponível":"Agendado"}</Badge>
              {s.status==="booked" && <span className="text-xs text-slate-500">{s.booked_by_name}</span>}
              {s.status==="available" && <Button variant="ghost" size="sm" onClick={()=>del(s.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// --------------------- Admin: moderate reviews ---------------------
export function AdminReviews() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const load = ()=> api.get("/admin/reviews",{params:{status_filter: status||undefined}}).then(r=>setItems(r.data));
  useEffect(() => { load(); }, [status]);
  const moderate = async (id, s) => { await api.patch(`/admin/reviews/${id}/moderate`,{status: s}); toast.success("Atualizado"); load(); };
  return (
    <Card className="p-5 gs-shadow" data-testid="admin-reviews">
      <div className="flex items-center gap-2 mb-4">
        <label className="text-xs text-slate-500">Filtrar</label>
        <select value={status} onChange={e=>setStatus(e.target.value)} className="h-9 rounded-md border px-2 text-sm">
          <option value="">Todas</option><option value="pending">Pendentes</option><option value="approved">Aprovadas</option><option value="rejected">Rejeitadas</option>
        </select>
      </div>
      <div className="space-y-2">
        {items.length===0 && <div className="text-sm text-slate-500 text-center py-6">Sem avaliações neste filtro.</div>}
        {items.map(r=>(
          <div key={r.id} className="flex items-start justify-between gap-4 border-b border-slate-100 py-3" data-testid={`admin-review-${r.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm"><b>{r.author_name}</b><Stars value={r.rating}/></div>
              {r.comment && <div className="text-sm text-slate-700 mt-1">{r.comment}</div>}
              <div className="text-xs text-slate-500 mt-1">Prof.: {r.professional_id} • {new Date(r.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className="bg-slate-100 text-slate-700 border-0">{r.status}</Badge>
              <Button size="sm" variant="outline" data-testid={`review-approve-${r.id}`} onClick={()=>moderate(r.id,"approved")}>Aprovar</Button>
              <Button size="sm" variant="outline" data-testid={`review-reject-${r.id}`} onClick={()=>moderate(r.id,"rejected")}>Rejeitar</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
